import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton.tsx';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton.tsx';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton.tsx';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton.tsx';
import { FollowSearch } from '../pdpMessages.tsx';
import { PdpSharedShoppingButtons } from '../shared/PdpSharedShoppingButtons/PdpSharedShoppingButtons.tsx';
import { PdpSharedDelimiter } from '../shared/PdpSharedDelimiter/PdpSharedDelimiter.tsx';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';

// helpers
import classNames from 'classnames';
import { getDisplayInfo } from '../helpers/getDisplayInfo.ts';
import {
    authLabels,
    locations,
    trackFollowSearchAdded,
    trackFollowSearchRemoved,
} from '../../utils/tracking/shared/followSearchTracking.ts';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { isMultiItemCheckoutVariant } from 'dibs-checkout/exports/useMultiItemCheckout.ts';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespSidebarButtons.scss';

import { type PdpRespSidebarButtons_user$key } from './__generated__/PdpRespSidebarButtons_user.graphql.ts';
import { type PdpRespSidebarButtons_item$key } from './__generated__/PdpRespSidebarButtons_item.graphql.ts';
import { type PdpRespSidebarButtons_itemSearch$key } from './__generated__/PdpRespSidebarButtons_itemSearch.graphql.ts';
import { type PdpRespSidebarButtons_displayInfo$key } from './__generated__/PdpRespSidebarButtons_displayInfo.graphql.ts';
import { type PdpRespSidebarButtons_viewer$key } from './__generated__/PdpRespSidebarButtons_viewer.graphql.ts';

type Props = {
    viewer: PdpRespSidebarButtons_viewer$key;
    user: PdpRespSidebarButtons_user$key | null;
    item: PdpRespSidebarButtons_item$key;
    itemSearch: PdpRespSidebarButtons_itemSearch$key | null | undefined;
    displayInfo: PdpRespSidebarButtons_displayInfo$key | null | undefined;
    relayVariables: {
        itemId: string;
        userId?: string | null;
        isMultiSku?: boolean;
    };
    quantitySelected?: string;
    skuId?: string;
    currency: string;
};

// messages
const followSearchText = (searchName: string): ReactNode => (
    <p className={styles.followSearchText}>
        <FollowSearch searchName={searchName} searchNameClassname={styles.searchName} />
    </p>
);

export const PdpRespSidebarButtons: FC<Props> = props => {
    const {
        relayVariables,
        viewer: viewerRef,
        user: userRef,
        item: itemRef,
        quantitySelected,
        skuId,
        itemSearch: itemSearchRef,
        displayInfo: displayInfoRef,
        currency,
    } = props;

    const viewer = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_viewer on Viewer {
                ...PdpSharedPurchaseButton_viewer
                showAddToCartButton: featureFlag(feature: "multiItemCartAddToCartButton")
            }
        `,
        viewerRef
    );

    const user = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_user on User {
                isVerifiedTrade
                ...PdpSharedContactDealerButton_user
                ...PdpSharedMakeOfferButton_user
                ...PdpSharedShoppingButtons_user
                ...SharedSaveSearchContext_user
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_item on Item {
                isSold
                isUnpurchasable
                saveSearchData {
                    name
                    page
                }
                ...PdpSharedContactDealerButton_item
                ...PdpSharedPurchaseButton_item
                ...PdpSharedMakeOfferButton_item
                skus @include(if: $isMultiSku) {
                    serviceId
                    displayInfo(page: pdp, buyerId: $userId) {
                        ctas {
                            showPurchase
                            showMakeOffer
                            showRequestPrice
                            showFollowCategory
                            showAcceptOffer
                        }
                    }
                }
                ...PdpSharedShoppingButtons_item
            }
        `,
        itemRef
    );
    const itemSearch = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_itemSearch on ItemSearchQueryConnection {
                ...SaveSearchButton_itemSearch
                ...SharedSaveSearchContext_itemSearch
            }
        `,
        itemSearchRef
    );
    const displayInfoInit = useFragment(
        graphql`
            fragment PdpRespSidebarButtons_displayInfo on ItemDisplayInfo {
                ctas {
                    showPurchase
                    showMakeOffer
                    showRequestPrice
                    showFollowCategory
                    showAcceptOffer
                }
            }
        `,
        displayInfoRef
    );
    const { itemId, userId, isMultiSku } = relayVariables;
    const isMultiItemCart = isMultiItemCheckoutVariant();
    const showAddToCartButton = !!viewer?.showAddToCartButton;
    const { isPDPDesktopAggregationVariant } =
        useAppSelector(state => state?.relayVariables?.variables || {}) || {};

    const displayInfo = getDisplayInfo({
        displayInfo: displayInfoInit,
        skus: item.skus,
        selectedSkuId: skuId,
        isMultiSku,
    });

    const { isSold: isSoldPdp, isUnpurchasable, saveSearchData } = item || {};
    const { isVerifiedTrade } = user || {};

    if (!displayInfo?.ctas || isSoldPdp || isUnpurchasable) {
        return null;
    }

    const { showPurchase, showMakeOffer, showRequestPrice, showFollowCategory, showAcceptOffer } =
        displayInfo.ctas;
    const buttonsClass = classNames({ [dibsCss.mtXsmall]: quantitySelected });

    const MakeOfferButton: FC = () =>
        showMakeOffer ? (
            <PdpSharedMakeOfferButton
                itemId={itemId}
                selectedSkuId={skuId}
                item={item}
                userId={userId}
                user={user}
                quantitySelected={quantitySelected}
                currency={currency}
                showArrow={!isPDPDesktopAggregationVariant}
                gaLabel="pdp main"
                hasPrivateOffer={showAcceptOffer}
            />
        ) : null;

    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    // secondary CTAs are the buttons below the title
    let secondaryCtas: ReactNode;

    if (showRequestPrice) {
        // Request price CTA is exclusive to the rest, so display it alone if showRequestPrice
        secondaryCtas = (
            <PdpSharedContactDealerButton
                type={'REQUEST_PRICE'}
                item={item}
                selectedSkuId={skuId}
                user={user}
            />
        );
    } else if (isMultiItemCart && showAddToCartButton && !showAcceptOffer) {
        // If multi item cart is enabled, display purchase and make offer buttons
        secondaryCtas = (
            <div className={classNames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXxsmall)}>
                {showPurchase && (
                    <PdpSharedShoppingButtons
                        user={user}
                        item={item}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                    />
                )}
                {showPurchase && showMakeOffer && <PdpSharedDelimiter />}
                <MakeOfferButton />
            </div>
        );
    } else {
        // Otherwise just display buttons as is
        secondaryCtas = (
            <>
                {showPurchase && (
                    <PdpSharedPurchaseButton
                        viewer={viewer}
                        itemId={itemId}
                        item={item}
                        userId={userId}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                        hasPrivateOffer={showAcceptOffer}
                    />
                )}
                <MakeOfferButton />
            </>
        );
    }

    return (
        <SharedSaveSearchContextProvider
            itemSearch={itemSearch}
            user={user}
            searchTerm={followSearchName || ''}
            uriRef={followSearchPage || ''}
        >
            <div className={buttonsClass} data-tn="item-actions">
                {secondaryCtas}

                {/* Follow Search */}
                {showFollowCategory && followSearchName && isVerifiedTrade ? (
                    // trade users
                    <div className={classNames(dibsCss.flex, dibsCss.mtSmall)}>
                        {followSearchText(followSearchName)}
                        <div>
                            <SaveSearchButton
                                itemSearch={itemSearch}
                                size="small"
                                locationLabel={isSoldPdp ? authLabels[locations.SOLD_PDP] : null}
                                onComplete={isFollowing =>
                                    isFollowing
                                        ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                        : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                                }
                            />
                        </div>
                    </div>
                ) : (
                    showFollowCategory &&
                    followSearchName && (
                        // non trade users
                        <div>
                            {followSearchText(followSearchName)}
                            <SaveSearchButton
                                itemSearch={itemSearch}
                                fullWidth
                                locationLabel={isSoldPdp ? authLabels[locations.SOLD_PDP] : null}
                                onComplete={isFollowing =>
                                    isFollowing
                                        ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                        : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                                }
                            />
                        </div>
                    )
                )}
            </div>
        </SharedSaveSearchContextProvider>
    );
};
